<template>
  <v-card style="min-height: 60vh" color="transparent" dark elevation="0" class="mx-auto">
    <v-overlay :value="loading" absolute z-index="10">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
      <div class="mt-4 text-center text-h6">{{ loadingMessage }}</div>
    </v-overlay>
    <v-card color="#1E1E1E" class="mb-8 pa-4 rounded-lg">
      <v-row align="center" justify="center">
        <v-col cols="12" md="5">
          <v-menu v-model="menuStart" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedStartDate"
                label="Startdatum"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                hide-details></v-text-field>
            </template>
            <v-date-picker v-model="startDate" @input="menuStart = false" :max="today" color="primary" no-title></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="5">
          <v-menu v-model="menuEnd" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedEndDate"
                label="Enddatum"
                prepend-inner-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                hide-details></v-text-field>
            </template>
            <v-date-picker v-model="endDate" @input="menuEnd = false" :max="today" color="primary" no-title></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" md="2" class="text-center">
          <v-btn color="primary" @click="fetchGrowthData" :loading="loading" large block class="font-weight-bold">
            <v-icon left>mdi-sync</v-icon>
            Laden
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <div v-if="loaded">
      <v-col cols="12">
        <v-card class="rounded-xl card-raised" hover dark>
          <v-card-title class="d-flex align-center">
            <span class="font-weight-bold white--text">Wachstumsverlauf</span>
          </v-card-title>
          <v-card-subtitle>{{ formattedDateRange }}</v-card-subtitle>
          <v-alert class="mx-5" type="info" text border="left" color="primary">
            Hinweis: Kommen neue Center dazu, machen die Abozahlen in diesem Monat einen Sprung – das Wachstumsziel wirkt dadurch übertroffen. Ab dem
            Folgemonat stimmt der Verlauf wieder. In der Aboentwicklung sind die vergangenen Zahlen mit den neuen Centern rückwirkend enthalten.
          </v-alert>
          <apexchart type="line" :options="growthChartOptions" :series="growthSeries"></apexchart>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card class="rounded-xl card-raised" hover dark>
          <v-card-title class="d-flex align-center">
            <span class="font-weight-bold white--text">Kunden pro QM</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewModes.customersPerQm" mandatory dense borderless>
              <v-btn icon value="chart"><v-icon>mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table"><v-icon>mdi-table</v-icon></v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-subtitle>{{ formattedDateRange }}</v-card-subtitle>
          <apexchart
            v-if="viewModes.customersPerQm === 'chart'"
            type="bar"
            :options="customersPerQmChartOptions"
            :series="customersPerQmSeries"></apexchart>
          <v-data-table
            v-else
            dense
            :items-per-page="-1"
            :headers="tableDataFor.customersPerQm.headers"
            :items="tableDataFor.customersPerQm.items"
            class="elevation-0 transparent mx-4 mb-4"
            dark></v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card class="rounded-xl card-raised" hover dark>
          <v-card-title class="d-flex align-center">
            <span class="font-weight-bold white--text">Kunden pro 100%</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewModes.customersPer100" mandatory dense borderless>
              <v-btn icon value="chart"><v-icon>mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table"><v-icon>mdi-table</v-icon></v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-subtitle>{{ formattedDateRange }}</v-card-subtitle>
          <apexchart
            v-if="viewModes.customersPer100 === 'chart'"
            type="bar"
            :options="customersPer100ChartOptions"
            :series="customersPer100Series"></apexchart>
          <v-data-table
            v-else
            dense
            :items-per-page="-1"
            :headers="tableDataFor.customersPer100.headers"
            :items="tableDataFor.customersPer100.items"
            class="elevation-0 transparent mx-4 mb-4"
            dark></v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-switch dark v-model="showCustomerInfosTable" label="Zusatzinformationen in der Haupttabelle anzeigen" class="white--text pa-2"></v-switch>
        <v-data-table dense :items-per-page="-1" :headers="headers" :items="tableData" class="elevation-1 card-raised" dark> </v-data-table>
      </v-col>

      <v-col cols="12">
        <v-divider class="my-6"></v-divider>
        <h2 class="text-h4 font-weight-bold mb-4 white--text">Vertrags- & Paket-Analyse</h2>
      </v-col>

      <v-col cols="12">
        <v-card class="rounded-xl card-raised" hover dark>
          <v-card-title class="d-flex align-center">
            <span class="font-weight-bold white--text">Neuverträge (Neumitglied & Ehemalig)</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewModes.newContracts" mandatory dense borderless>
              <v-btn icon value="chart"><v-icon>mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table"><v-icon>mdi-table</v-icon></v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-subtitle>{{ formattedDateRange }}</v-card-subtitle>
          <apexchart
            v-if="viewModes.newContracts === 'chart'"
            type="bar"
            :options="newContractsChartOptions"
            :series="newContractsSeries"></apexchart>
          <v-data-table
            v-else
            dense
            :items-per-page="-1"
            :headers="[
              { text: 'Studio', value: 'name' },
              { text: 'Anzahl', value: 'value' },
            ]"
            :items="tableDataFor.newContracts"
            class="elevation-0 transparent mx-4 mb-4"
            dark></v-data-table>
        </v-card>
      </v-col>

      <v-col v-if="false" cols="12">
        <v-card class="rounded-xl card-raised" hover dark>
          <v-card-title class="d-flex align-center">
            <span class="font-weight-bold white--text">Vertragserneuerungen</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewModes.renewals" mandatory dense borderless>
              <v-btn icon value="chart"><v-icon>mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table"><v-icon>mdi-table</v-icon></v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-subtitle>{{ formattedDateRange }}</v-card-subtitle>
          <apexchart v-if="viewModes.renewals === 'chart'" type="bar" :options="renewalsChartOptions" :series="renewalsSeries"></apexchart>
          <v-data-table
            v-else
            dense
            :items-per-page="-1"
            :headers="[
              { text: 'Studio', value: 'name' },
              { text: 'Anzahl', value: 'value' },
            ]"
            :items="tableDataFor.renewals"
            class="elevation-0 transparent mx-4 mb-4"
            dark></v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card class="rounded-xl card-raised" hover dark>
          <v-card-title class="d-flex align-center">
            <span class="font-weight-bold white--text">Vertrags-Upgrades</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewModes.upgrades" mandatory dense borderless>
              <v-btn icon value="chart"><v-icon>mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table"><v-icon>mdi-table</v-icon></v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-subtitle>{{ formattedDateRange }}</v-card-subtitle>
          <apexchart v-if="viewModes.upgrades === 'chart'" type="bar" :options="upgradesChartOptions" :series="upgradesSeries"></apexchart>
          <v-data-table
            v-else
            dense
            :items-per-page="-1"
            :headers="[
              { text: 'Studio', value: 'name' },
              { text: 'Anzahl', value: 'value' },
            ]"
            :items="tableDataFor.upgrades"
            class="elevation-0 transparent mx-4 mb-4"
            dark></v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card class="rounded-xl card-raised" hover dark>
          <v-card-title class="d-flex align-center">
            <span class="font-weight-bold white--text">Verkaufte Zusatzpakete (aus Neuverträgen & Upgrades)</span>
            <v-spacer></v-spacer>
            <v-btn-toggle v-model="viewModes.packages" mandatory dense borderless>
              <v-btn icon value="chart"><v-icon>mdi-chart-bar</v-icon></v-btn>
              <v-btn icon value="table"><v-icon>mdi-table</v-icon></v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-subtitle>{{ formattedDateRange }}</v-card-subtitle>
          <apexchart v-if="viewModes.packages === 'chart'" type="bar" :options="packagesChartOptions" :series="packagesSeries"></apexchart>
          <v-data-table
            v-else
            dense
            :items-per-page="-1"
            :headers="tableDataFor.packages.headers"
            :items="tableDataFor.packages.items"
            class="elevation-0 transparent mx-4 mb-4"
            dark></v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-divider class="my-6"></v-divider>
        <h2 class="text-h4 font-weight-bold mb-4 white--text">Laufende Zusatzpakete</h2>
      </v-col>
      <v-col cols="12">
        <v-card v-if="latestPackageStats && latestPackageStats.date" class="rounded-xl card-raised" hover dark>
          <v-card-title>
            <span class="font-weight-bold white--text">Aktueller Bestand pro Studio</span>
          </v-card-title>
          <v-card-subtitle v-if="latestPackageStats.date">Stand: {{ moment(latestPackageStats.date).format("DD.MM.YYYY") }}</v-card-subtitle>
          <v-data-table
            dense
            :items-per-page="-1"
            :headers="latestPackagesHeaders"
            :items="latestPackagesItems"
            class="elevation-0 transparent mx-4 mb-4"
            dark
            hide-default-footer>
            <template v-slot:body.append>
              <tr class="total-row font-weight-bold">
                <td>Total</td>
                <td v-for="header in latestPackagesHeaders.slice(1, -1)" :key="header.value" class="text-right">
                  {{ latestPackagesTotals[header.value] }}
                </td>
                <td class="text-right">
                  {{ latestPackagesOverallTotal }}
                </td>
              </tr>
            </template>
            <template v-slot:item="{ item }">
              <tr>
                <td>{{ item.Studio }}</td>
                <td v-for="header in latestPackagesHeaders.slice(1, -1)" :key="header.value" class="text-right">
                  {{ item[header.value] }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ item.Total }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </div>
  </v-card>
</template>

<script>
import api from "@/api"
import moment from "moment"

export default {
  name: "growthStatistics",
  props: {
    studios: { type: Array, default: () => [] },
  },
  data() {
    return {
      today: new Date().toISOString().substr(0, 10),
      startDate: new Date("2025-01-01").toISOString().substr(0, 10),
      endDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      menuStart: false,
      menuEnd: false,
      contractStatistics: {
        studioNames: [],
        newContractsSeries: [],
        renewalsSeries: [],
        upgradesSeries: [],
        packagesSeries: [],
      },
      latestPackageStats: { date: null, stats: [] },
      loading: false,
      loaded: false,
      growthData: {},
      showCustomerInfosTable: false,
      viewModes: {
        customersPerQm: "chart",
        customersPer100: "chart",
        newContracts: "chart",
        renewals: "chart",
        upgrades: "chart",
        packages: "chart",
      },
      // ⚙️ NEUE DATEN FÜR DIE LADE-ANIMATION ⚙️
      loadingMessages: [
        "Lade Wachstumsdaten...",
        "Analysiere Vertragsstatistiken...",
        "Prüfe aktuelle Zusatzpakete...",
        "Die Zahlen werden poliert...",
        "Gleich geschafft!",
      ],
      loadingMessage: "Lade Daten...",
      loadingInterval: null,
    }
  },
  computed: {
    moment() {
      return moment
    },
    formattedStartDate() {
      return moment(this.startDate).format("DD.MM.YYYY")
    },
    formattedEndDate() {
      return moment(this.endDate).format("DD.MM.YYYY")
    },
    formattedDateRange() {
      return `Zeitraum: ${this.formattedStartDate} - ${this.formattedEndDate}`
    },
    sortedContractData() {
      const stats = this.contractStatistics
      if (!stats || !stats.studioNames || !stats.studioNames.length) return {}

      const sortable = stats.studioNames.map((name, index) => {
        const totalPackages = (stats.packagesSeries || []).reduce((sum, series) => sum + (series.data[index] || 0), 0)
        return {
          name,
          newContracts: stats.newContractsSeries[0]?.data[index] || 0,
          renewals: stats.renewalsSeries[0]?.data[index] || 0,
          upgrades: stats.upgradesSeries[0]?.data[index] || 0,
          totalPackages,
          packages: (stats.packagesSeries || []).map((series) => ({ name: series.name, data: series.data[index] })),
        }
      })

      const createSortedData = (key) => {
        if (!sortable.length) return { names: [], seriesData: [], tableData: [] }
        const sorted = [...sortable].sort((a, b) => b[key] - a[key])
        return {
          names: sorted.map((s) => s.name),
          seriesData: sorted.map((s) => s[key]),
          tableData: sorted.map((s) => ({ name: s.name, value: s[key] })),
        }
      }

      const packagesSorted = [...sortable].sort((a, b) => b.totalPackages - a.totalPackages)

      return {
        newContracts: createSortedData("newContracts"),
        renewals: createSortedData("renewals"),
        upgrades: createSortedData("upgrades"),
        packages: {
          names: packagesSorted.map((s) => s.name),
          series: (stats.packagesSeries || []).map((series) => ({
            name: series.name,
            data: packagesSorted.map((s) => (s.packages.find((p) => p.name === series.name) || {}).data || 0),
          })),
        },
      }
    },
    tableDataFor() {
      const stats = this.contractStatistics
      const growthStats = this.chartData
      if (!stats || !stats.studioNames || !this.sortedContractData.packages) return { packages: { headers: [], items: [] } }

      const packagesHeaders = [
        { text: "Studio", value: "name", sortable: true },
        ...(stats.packagesSeries || []).map((s) => ({ text: s.name, value: s.name, sortable: true })),
      ]

      const packagesItems = (this.sortedContractData.packages?.names || []).map((name) => {
        const row = { name }
        const studioIndex = stats.studioNames.indexOf(name)
        ;(stats.packagesSeries || []).forEach((series) => {
          row[series.name] = series.data[studioIndex] || 0
        })
        return row
      })

      return {
        newContracts: this.sortedContractData.newContracts?.tableData || [],
        renewals: this.sortedContractData.renewals?.tableData || [],
        upgrades: this.sortedContractData.upgrades?.tableData || [],
        packages: { headers: packagesHeaders, items: packagesItems },
        customersPerQm: {
          headers: [
            { text: "Studio", value: "studioName" },
            { text: "Start", value: "startCustomersPerQm" },
            { text: "Ende", value: "endCustomersPerQm" },
          ],
          items: [...growthStats].sort((a, b) => parseFloat(b.endCustomersPerQm) - parseFloat(a.endCustomersPerQm)),
        },
        customersPer100: {
          headers: [
            { text: "Studio", value: "studioName" },
            { text: "Start", value: "startCustomersPer100" },
            { text: "Ende", value: "endCustomersPer100" },
          ],
          items: [...growthStats].sort((a, b) => parseFloat(b.endCustomersPer100) - parseFloat(a.endCustomersPer100)),
        },
      }
    },
    newContractsSeries() {
      return [{ name: "Neuverträge", data: this.sortedContractData.newContracts?.seriesData || [] }]
    },
    newContractsChartOptions() {
      return {
        chart: { type: "bar", foreColor: "#ccc" },
        xaxis: { categories: this.sortedContractData.newContracts?.names || [] },
        tooltip: { theme: "dark" },
        colors: ["#00E396"],
        plotOptions: { bar: { horizontal: false } },
        legend: { show: false },
      }
    },
    renewalsSeries() {
      return [{ name: "Erneuerungen", data: this.sortedContractData.renewals?.seriesData || [] }]
    },
    renewalsChartOptions() {
      return {
        chart: { type: "bar", foreColor: "#ccc" },
        xaxis: { categories: this.sortedContractData.renewals?.names || [] },
        tooltip: { theme: "dark" },
        colors: ["#008FFB"],
        plotOptions: { bar: { horizontal: false } },
        legend: { show: false },
      }
    },
    upgradesSeries() {
      return [{ name: "Upgrades", data: this.sortedContractData.upgrades?.seriesData || [] }]
    },
    upgradesChartOptions() {
      return {
        chart: { type: "bar", foreColor: "#ccc" },
        xaxis: { categories: this.sortedContractData.upgrades?.names || [] },
        tooltip: { theme: "dark" },
        colors: ["#FF4560"],
        plotOptions: { bar: { horizontal: false } },
        legend: { show: false },
      }
    },
    packagesSeries() {
      return this.sortedContractData.packages?.series || []
    },
    packagesChartOptions() {
      return {
        chart: { type: "bar", stacked: true, foreColor: "#ccc" },
        xaxis: { categories: this.sortedContractData.packages?.names || [] },
        tooltip: { theme: "dark" },
        plotOptions: { bar: { horizontal: false } },
        legend: { position: "top", horizontalAlign: "left", formatter: (s) => (s.length > 20 ? s.substring(0, 20) + "..." : s) },
      }
    },
    headers() {
      let baseHeaders = [
        { text: "Studio", value: "studioName" },
        { text: "Kunden (Start)", value: "startCustomers" },
        { text: "Kunden (Ende)", value: "endCustomers" },
        { text: "Wachstum (%)", value: "growthPercentage" },
        { text: "Ziel 2025", value: "goal2025" },
        { text: "Wachstum bis Ziel (%)", value: "growthToGoalPercentage" },
      ]
      if (this.showCustomerInfosTable) {
        baseHeaders.splice(
          4,
          0,
          { text: "Kunden/QM (Start)", value: "startCustomersPerQm" },
          { text: "Kunden/QM (Ende)", value: "endCustomersPerQm" },
          { text: "Kunden/100% (Start)", value: "startCustomersPer100" },
          { text: "Kunden/100% (Ende)", value: "endCustomersPer100" }
        )
      }
      return baseHeaders
    },
    chartData() {
      return (this.growthData.studios || []).filter((d) => d.studioName !== "Gesamt")
    },
    growthChartOptions() {
      return {
        chart: { id: "growth-chart", foreColor: "#ccc" },
        xaxis: {
          type: "datetime",
          categories: (this.growthData.timeline || []).map((d) => d.date),
        },
        tooltip: { theme: "dark", x: { format: "dd.MM.yyyy" } },
      }
    },
    growthSeries() {
      return [
        { name: "Ziel", data: (this.growthData.timeline || []).map((d) => d.goal) },
        { name: "Erreicht", data: (this.growthData.timeline || []).map((d) => d.actual) },
      ]
    },
    customersPerQmChartOptions() {
      const sortedData = [...this.chartData].sort((a, b) => parseFloat(b.endCustomersPerQm) - parseFloat(a.endCustomersPerQm))
      return {
        chart: { id: "customer-per-qm-chart", foreColor: "#ccc" },
        xaxis: { categories: sortedData.map((d) => d.studioName) },
        tooltip: { theme: "dark" },
        plotOptions: { bar: { dataLabels: { position: "top" } } },
        dataLabels: { enabled: true, offsetY: -20, style: { fontSize: "12px", colors: ["#FFFFFF"] } },
      }
    },
    customersPerQmSeries() {
      const sortedData = [...this.chartData].sort((a, b) => parseFloat(b.endCustomersPerQm) - parseFloat(a.endCustomersPerQm))
      return [
        { name: "Kunden/QM (Start)", data: sortedData.map((d) => d.startCustomersPerQm) },
        { name: "Kunden/QM (Ende)", data: sortedData.map((d) => d.endCustomersPerQm) },
      ]
    },
    customersPer100ChartOptions() {
      const sortedData = [...this.chartData].sort((a, b) => parseFloat(b.endCustomersPer100) - parseFloat(a.endCustomersPer100))
      return {
        chart: { id: "customer-per-100-chart", foreColor: "#ccc" },
        xaxis: { categories: sortedData.map((d) => d.studioName) },
        tooltip: { theme: "dark" },
        plotOptions: { bar: { dataLabels: { position: "top" } } },
        dataLabels: { enabled: true, offsetY: -20, style: { fontSize: "12px", colors: ["#FFFFFF"] } },
      }
    },
    customersPer100Series() {
      const sortedData = [...this.chartData].sort((a, b) => parseFloat(b.endCustomersPer100) - parseFloat(a.endCustomersPer100))
      return [
        { name: "Kunden/100% (Start)", data: sortedData.map((d) => d.startCustomersPer100) },
        { name: "Kunden/100% (Ende)", data: sortedData.map((d) => d.endCustomersPer100) },
      ]
    },
    tableData() {
      return this.growthData.studios || []
    },
    latestPackagesAllNames() {
      if (!this.latestPackageStats.stats) return []
      const packageSet = new Set()
      this.latestPackageStats.stats.forEach((studio) => {
        studio.Packages.forEach((pkg) => packageSet.add(pkg.NAME))
      })
      return Array.from(packageSet).sort()
    },
    latestPackagesHeaders() {
      return [
        { text: "Studio", value: "Studio", align: "start" },
        ...this.latestPackagesAllNames.map((name) => ({ text: name, value: name, align: "end" })),
        { text: "Total", value: "Total", align: "end" },
      ]
    },
    latestPackagesItems() {
      if (!this.latestPackageStats.stats) return []
      return this.latestPackageStats.stats
        .map((studio) => {
          const row = { Studio: studio.Studio }
          let total = 0
          this.latestPackagesAllNames.forEach((pkgName) => {
            const pkg = studio.Packages.find((p) => p.NAME === pkgName)
            const count = pkg ? pkg.TotalCount : 0
            row[pkgName] = count
            total += count
          })
          row.Total = total
          return row
        })
        .sort((a, b) => b.Total - a.Total)
    },
    latestPackagesTotals() {
      const totals = {}
      this.latestPackagesAllNames.forEach((name) => {
        totals[name] = this.latestPackagesItems.reduce((sum, item) => sum + (item[name] || 0), 0)
      })
      return totals
    },
    latestPackagesOverallTotal() {
      return this.latestPackagesItems.reduce((sum, item) => sum + item.Total, 0)
    },
  },
  methods: {
    async fetchGrowthData() {
      this.loading = true

      // --- ⚙️ LADE-NACHRICHTEN STARTEN ⚙️ ---
      let messageIndex = 0
      this.loadingMessage = this.loadingMessages[messageIndex] // Sofort die erste Nachricht anzeigen
      this.loadingInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % this.loadingMessages.length
        this.loadingMessage = this.loadingMessages[messageIndex]
      }, 2500) // Alle 2.5 Sekunden wechseln

      try {
        const [growthResponse, contractResponse, latestPackagesResponse] = await Promise.all([
          api.fetchGrowthStatistics({ startDate: this.startDate, endDate: this.endDate, studios: this.studios.map((s) => s._id) }),
          api.fetchContractStatistics({ startDate: this.startDate, endDate: this.endDate, studios: this.studios.map((s) => s._id) }),
          api.fetchLatestPackageStats(),
        ])
        this.growthData = growthResponse
        this.contractStatistics = contractResponse
        this.latestPackageStats = latestPackagesResponse
        this.loaded = true
      } catch (error) {
        console.error("Fehler beim Laden der Statistiken:", error)
        this.$toast.error("Fehler beim Laden der Statistiken")
      } finally {
        // --- ⚙️ LADE-NACHRICHTEN STOPPEN (SEHR WICHTIG!) ⚙️ ---
        clearInterval(this.loadingInterval)
        this.loading = false
      }
    },
  },
  created() {
    // this.fetchGrowthData()
  },
}
</script>

<style scoped>
.card-raised {
  background-color: rgba(45, 45, 45, 0.6);
  transition: all 0.2s ease-in-out;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.card-raised:hover {
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.4);
  transform: translateY(-4px);
}
.total-row {
  background-color: rgba(255, 255, 255, 0.1) !important;
}
.v-data-table::v-deep .v-data-footer {
  display: none;
}
</style>
